import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  XMarkIcon,
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import LogisticsLogo from '../../assets/logistics_logo.svg';
import { useAuth } from '../../contexts/AuthContext';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { logout, user } = useAuth();
  // Get current page title based on path
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path === '/requests') return 'All Requests';
    if (path === '/analytics') return 'Analytics';
    if (path.startsWith('/requests/')) return 'Request Details';
    if (path === '/new-supplier') return 'New Supplier';
    if (path === '/suppliers') return 'Suppliers';
    if (path === '/po-items') return 'PO Items Search';
    if (path === '/addresses') return 'Address Database';
    return 'TFS';
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'All Requests', href: '/requests', icon: DocumentTextIcon },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
    { name: 'PO Items Search', href: '/po-items', icon: MagnifyingGlassIcon },
    { name: 'Address Database', href: '/addresses', icon: UserGroupIcon },
    { name: 'New Supplier', href: '/new-supplier', icon: PlusCircleIcon },
    { name: 'Suppliers', href: '/suppliers', icon: UserGroupIcon }
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
          <div className="flex h-16 shrink-0 items-center justify-between px-4 border-b border-gray-200">
            <div className="flex items-center">
              <img src={LogisticsLogo} alt="TFS Logo" className="h-8 w-auto" />
              <span className="ml-2 text-xl font-bold text-gray-900">TFS</span>
            </div>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </nav>
          
          {/* Mobile logout button */}
          <div className="px-2 pb-4">
            <button
              onClick={handleLogout}
              className="group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-red-50 hover:text-red-600"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 flex-shrink-0" />
              Logout
            </button>
            {user && user.username ? (
               <div className="mt-2 px-3 py-2 text-xs text-gray-500 border-t border-gray-200">
                 Signed in as {user.username} 
               </div>
             ) : (
               <div className="mt-2 px-3 py-2 text-xs text-gray-500 border-t border-gray-200">
                 Signed in (User data loading...)
               </div>
             )}
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow border-r border-gray-200 bg-white pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6">
            <img src={LogisticsLogo} alt="TFS Logo" className="h-10 w-auto" />
            <span className="ml-2 text-xl font-bold text-gray-900">TFS</span>
          </div>
          <div className="mt-8 flex-grow flex flex-col">
            <nav className="flex-1 px-4 space-y-2">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                    }`
                  }
                >
                  <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </NavLink>
              ))}
            </nav>
            
            {/* Desktop logout section */}
            <div className="px-4 pb-4">
              <button
                onClick={handleLogout}
                className="group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-red-50 hover:text-red-600"
              >
                <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 flex-shrink-0" />
                Logout
              </button>
              {user && user.username ? (
                 <div className="mt-2 px-3 py-2 text-xs text-gray-500 border-t border-gray-200">
                   Signed in as {user.username}
                 </div>
               ) : (
                 <div className="mt-2 px-3 py-2 text-xs text-gray-500 border-t border-gray-200">
                   Signed in (User data loading...)
                 </div>
               )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="sticky top-0 z-10 lg:hidden">
        <div className="flex items-center justify-between h-16 bg-white shadow-sm px-4">
          <div className="flex items-center">
            <button
              type="button"
              className="text-gray-500 hover:text-gray-600 focus:outline-none"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div className="ml-4 flex items-center">
              <img src={LogisticsLogo} alt="TFS Logo" className="h-8 w-auto" />
              <span className="ml-2 text-lg font-semibold text-gray-900">{getPageTitle()}</span>
            </div>
          </div>
          
          {/* Mobile header logout button */}
          <button
            onClick={handleLogout}
            className="text-gray-500 hover:text-red-600 focus:outline-none"
            aria-label="Logout"
          >
            <ArrowRightOnRectangleIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Page content */}
        <main className="flex-1">
          <div className="py-4 sm:py-6">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
